import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Camera, X, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';
import html2canvas from 'html2canvas';
import { collectBugContext } from '@/lib/feedback/errorBuffer';

interface BugReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function BugReportDialog({ isOpen, onClose }: BugReportDialogProps) {
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleScreenshotSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setScreenshot(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setScreenshotPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCaptureScreen = async () => {
    try {
      // Hide all dialog overlays and content so the screenshot captures the actual view
      const dialogOverlays = document.querySelectorAll<HTMLElement>('[data-radix-dialog-overlay], [role="dialog"], [data-radix-dialog-content]');
      dialogOverlays.forEach(el => {
        el.dataset.hiddenForScreenshot = el.style.display;
        el.style.display = 'none';
      });

      // Brief delay so the browser can repaint without the modal
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(document.body, {
        backgroundColor: '#F5F5F4',
        scale: 1,
        useCORS: true,
        allowTaint: true,
        logging: false,
        foreignObjectRendering: false,
        ignoreElements: (element: Element) => {
          // Skip any remaining dialog/overlay artifacts
          if (element.hasAttribute('data-radix-dialog-overlay')) return true;
          if (element.getAttribute('role') === 'dialog') return true;
          return false;
        },
      });

      // Restore dialog visibility
      dialogOverlays.forEach(el => {
        el.style.display = el.dataset.hiddenForScreenshot || '';
        delete el.dataset.hiddenForScreenshot;
      });

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'screenshot.png', { type: 'image/png' });
          setScreenshot(file);
          setScreenshotPreview(canvas.toDataURL());
        }
      });
    } catch {
      // Restore dialog on error
      const dialogOverlays = document.querySelectorAll<HTMLElement>('[data-radix-dialog-overlay], [role="dialog"], [data-radix-dialog-content]');
      dialogOverlays.forEach(el => {
        el.style.display = el.dataset.hiddenForScreenshot || '';
        delete el.dataset.hiddenForScreenshot;
      });
      fileInputRef.current?.click();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setScreenshot(file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setScreenshotPreview(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast({
        title: "Description required",
        description: "Please describe the issue you encountered.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      let screenshotUrl = null;
      
      if (screenshot) {
        const fileName = `${user?.id || 'anonymous'}-${Date.now()}.png`;
        const { error: uploadError } = await supabase.storage
          .from('bug-screenshots')
          .upload(fileName, screenshot);
        
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('bug-screenshots')
          .getPublicUrl(fileName);
        
        screenshotUrl = publicUrl;
      }

      const report = {
        user_id: user?.id,
        user_email: user?.email,
        description: description.trim(),
        screenshot_url: screenshotUrl,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      };
      // Route, build, viewport and the last console errors, so the report
      // can be reproduced without a back-and-forth with the user
      let { error } = await supabase
        .from('bug_reports')
        .insert({ ...report, context: collectBugContext() as unknown as Json });
      // Database without the context column yet (migration not applied):
      // still file the report rather than lose it
      if (error && /context/i.test(error.message)) {
        ({ error } = await supabase.from('bug_reports').insert(report));
      }

      if (error) throw error;

      setShowSuccess(true);
      
      setTimeout(() => {
        setDescription('');
        setScreenshot(null);
        setScreenshotPreview(null);
        setShowSuccess(false);
        onClose();
      }, 2500);

    } catch (err) {
      console.error('Bug report error:', err);
      toast({
        title: "Submission failed",
        description: "Couldn't submit your report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setDescription('');
      setScreenshot(null);
      setScreenshotPreview(null);
      setShowSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {showSuccess ? (
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle className="w-16 h-16 text-success mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Thank you!
            </h3>
            <p className="text-muted-foreground text-center">
              That meant a lot! - Tim
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Report a Bug</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  What went wrong?
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the issue you encountered..."
                  rows={4}
                  className="resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Screenshot (optional)
                </label>
                
                {screenshotPreview ? (
                  <div className="relative">
                    <img
                      src={screenshotPreview}
                      alt="Screenshot preview"
                      className="w-full h-40 object-cover rounded-lg border"
                    />
                    <button
                      onClick={removeScreenshot}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleCaptureScreen}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    className="w-full h-32 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-2 hover:border-primary/50 hover:bg-wash transition-colors"
                  >
                    <Camera className="w-8 h-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Click to capture or drag an image here
                    </span>
                  </button>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshotSelect}
                  className="hidden"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !description.trim()}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Report'
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
